import React, { useState, useEffect } from 'react';
import * as PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { useTranslation } from 'react-i18next';

import S from './modal-business-group.module.scss';
import { CREATE_BUSINESS_GROUP_GQL } from '../../../graphql/mutations/business-group-create';
import { UPDATE_BUSINESS_GROUP_GQL } from '../../../graphql/mutations/business-group-update';
import { BUSINESS_GROUP_BY_ID_GQL } from '../../../graphql/queries/business-group-by-id-query';
import { toast } from 'react-toastify';

export const ModalBusinessGroupForm = ({ handleClose, handleOnSuccess, id }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditMode = !!id;

  // Fetch business group data if in edit mode
  const { data: businessGroupData, loading: businessGroupLoading } = useQuery(
    BUSINESS_GROUP_BY_ID_GQL,
    {
      variables: { id },
      skip: !id
    }
  );

  // Set name from fetched data when in edit mode
  useEffect(() => {
    if (businessGroupData?.business_group) {
      setName(businessGroupData.business_group.name);
    }
  }, [businessGroupData?.business_group]);

  const onCompleted = () => {
    if (handleOnSuccess) {
      handleOnSuccess();
    }
    handleClose();
  };

  const onError = (error) => {
    toast.error(t('errors.generic'));
    setIsSubmitting(false);
  };

  const [createBusinessGroup] = useMutation(CREATE_BUSINESS_GROUP_GQL, {
    onCompleted,
    onError
  });

  const [updateBusinessGroup] = useMutation(UPDATE_BUSINESS_GROUP_GQL, {
    onCompleted,
    onError
  });

  const handleNameChange = (e) => {
    setName(e.target.value);
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      setError(t('businessGroupsPage.name_required'));
      return;
    }

    setIsSubmitting(true);

    if (isEditMode) {
      await updateBusinessGroup({
        variables: {
          id,
          name: name.trim()
        }
      });
    } else {
      await createBusinessGroup({
        variables: {
          name: name.trim()
        }
      });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <h2 className={S.formTitle}>
        {isEditMode
          ? t('businessGroupsPage.edit_group')
          : t('businessGroupsPage.add_new_group')}
      </h2>

      <div className={S.formGroup}>
        <label htmlFor="name" className={S.formLabel}>
          {t('businessGroupsPage.group_name')}
        </label>
        <input
          id="name"
          type="text"
          className={S.formInput}
          value={name}
          onChange={handleNameChange}
          placeholder={t('businessGroupsPage.enter_group_name')}
          disabled={isSubmitting || businessGroupLoading}
        />
        {error && <div className={S.errorMessage}>{error}</div>}
      </div>

      <div className={S.formActions}>
        <button
          type="button"
          className={S.cancelButton}
          onClick={handleClose}
          disabled={isSubmitting}
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          className={S.submitButton}
          disabled={isSubmitting || !name.trim() || businessGroupLoading}
        >
          {isSubmitting
            ? (isEditMode ? t('common.updating') : t('common.creating'))
            : (isEditMode ? t('common.update') : t('common.create'))}
        </button>
      </div>
    </form>
  );
};

ModalBusinessGroupForm.propTypes = {
  handleClose: PropTypes.func.isRequired,
  handleOnSuccess: PropTypes.func,
  id: PropTypes.string
};
